import { inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { WcgMenuOption } from '@wcg/frontend-framework';
import { Country } from '../types';

import {
  AddressBodyPayload,
  AddressForm,
  AddressLineGroup,
  AddressMode,
  AddressNew,
} from '../types';
import {
  countryCodeRegexNumericRegex,
  countryCodeWithAlphabetsRegex,
} from '../constants';

export function generateAddressFormGroup() {
  const _formBuilder = inject(FormBuilder);
  return _formBuilder.group<AddressForm>({
    addressLines: _formBuilder.array<AddressLineGroup>([]),
    countryId: new FormControl<WcgMenuOption | null>(null, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    state: new FormControl<WcgMenuOption | string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    zipCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
}

export function flattenAddressGroup(
  fGroup: FormGroup<AddressForm>,
  countries: Country[],
  mode: AddressMode,
  adressId?: string,
): AddressBodyPayload | null | AddressNew {
  const _fRawValue = fGroup.getRawValue();
  const _addressLines = {} as Pick<
    AddressBodyPayload,
    'address1' | 'address2' | 'address3' | 'city'
  >;
  if (!_fRawValue) {
    return null;
  }
  for (const line of _fRawValue.addressLines) {
    if (typeof line === 'object') {
      for (const [key, value] of Object.entries(line)) {
        Object.assign(_addressLines, { [key]: value });
      }
    }
  }
  const _state =
    typeof _fRawValue.state === 'object'
      ? _fRawValue.state['label']
      : _fRawValue.state;
  const _country = countries.reduce((acc, country) => {
    if (country.id === _fRawValue.countryId?.id) {
      Object.assign(acc, { ...country });
    }
    return acc;
  }, {} as Country);

  if (mode === 'edit') {
    return {
      ..._addressLines,
      country: _country,
      id: adressId,
      state: _state,
      zipCode: _fRawValue.zipCode,
    } as AddressNew;
  }

  return {
    ..._addressLines,
    countryId: _fRawValue.countryId?.id ?? '',
    state: _state,
    zipCode: _fRawValue.zipCode,
  } as AddressBodyPayload;
}

/**
 * Extracts the country code from a given phone number string.
 *
 * @returns The extracted country code (e.g. "+34"), or null if not found
 * @param countryCode
 * @param countries
 * @param countriess
 */

export function mapCountryCode(countryCode: string, countries: string[]) {
  if (!countryCode?.length) return null;
  if (countryCodeWithAlphabetsRegex.test(countryCode)) {
    return countries.find((cCode) => cCode.trim() === countryCode);
  }

  if (countryCodeRegexNumericRegex.test(countryCode)) {
    const _cCode = countryCode.trim().match(countryCodeRegexNumericRegex)?.[0];
    return _cCode ? countries.find((code) => code.includes(_cCode)) : null;
  }
  return null;
}

export function extractPhoneNumber(phoneNumber: string) {
  if (!phoneNumber?.length) return '';

  // Use libphonenumber-js to correctly identify country code length
  const parsed = parsePhoneNumberFromString(phoneNumber);
  if (parsed) {
    return parsed.nationalNumber;
  }

  // Fallback to old logic if parsing fails
  const _countryCodeRegex = /^\+\d{1,3}\s*/;
  let number = phoneNumber.trim().replace(_countryCodeRegex, '').trim();
  if (number.startsWith('-')) {
    number = number.slice(1).trim();
  }
  return number;
}

export function formatPhoneNumberWithDashes(phoneNumber: string) {
  if (!phoneNumber?.length) return '';
  const _countryCodeRegex = /^\+\d{1,3}\s*/;
  const raw = phoneNumber.trim().replace(_countryCodeRegex, '').trim();
  const digits = raw.replace(/\D+/g, '');

  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return digits;
}

export function extractJustCountryCode(phoneNumber: string) {
  if (!phoneNumber?.length) return '';
  const phoneMatch = phoneNumber.match(/(\+\d{1,3})\s*[\d\s\-().]+/);
  let phoneNumberPart = phoneMatch ? phoneMatch[0].trim() : phoneNumber;
  if (!phoneMatch && phoneNumber.includes('+')) {
    phoneNumberPart = phoneNumber.substring(phoneNumber.indexOf('+')).trim();
  }
  return phoneNumberPart;
}

export function compareObjectsFn(
  o1: WcgMenuOption,
  o2: WcgMenuOption,
): boolean {
  return o1.id?.toLowerCase() === o2.id?.toLowerCase();
}
